import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout, { PageHeader } from "@/components/Layout";
import { api, fmtINR, fmtNum } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { FileText, Calculator, Scale, Users, ArrowUpRight } from "lucide-react";

function Stat({ label, value, unit, icon: Icon, testid }) {
  return (
    <div className="panel p-6" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="label-tech">{label}</div>
        <Icon size={18} className="text-ink-faint" strokeWidth={1.5} />
      </div>
      <div className="mt-4 flex items-baseline gap-2">
        <div className="mono text-3xl font-bold text-ink tracking-tight">{value}</div>
        {unit && <div className="label-tech">{unit}</div>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/stats"), api.get("/quotations")]).then(([s, q]) => {
      setStats(s.data);
      setRecent(q.data.slice(0, 5));
    });
  }, []);

  return (
    <Layout>
      <PageHeader
        eyebrow={`Welcome · ${user.role}`}
        title={`Hello, ${user.name.split(" ")[0]}`}
        subtitle="Overview of quotations, weight fabricated and pipeline value."
        actions={
          <button
            onClick={() => navigate("/configurator")}
            className="btn-primary"
            data-testid="dash-new-config"
          >
            New configuration
            <ArrowUpRight size={16} />
          </button>
        }
      />

      <div className="p-8 space-y-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line">
          <Stat
            label="Quotations"
            value={stats ? stats.quotations : "—"}
            unit="total"
            icon={FileText}
            testid="stat-quotations"
          />
          <Stat
            label="Pipeline value"
            value={stats ? fmtINR(stats.total_value) : "—"}
            icon={Calculator}
            testid="stat-value"
          />
          <Stat
            label="Steel fabricated"
            value={stats ? fmtNum(stats.total_weight_kg, 1) : "—"}
            unit="kg"
            icon={Scale}
            testid="stat-weight"
          />
          <Stat
            label={user.role === "admin" ? "Users" : "Your role"}
            value={
              user.role === "admin"
                ? stats
                  ? stats.users
                  : "—"
                : user.role.toUpperCase()
            }
            icon={Users}
            testid="stat-users"
          />
        </div>

        <div className="grid grid-cols-1 gap-8">
          <div className="panel" data-testid="recent-panel">
            <div className="flex items-center justify-between px-5 py-4 border-b border-line">
              <div className="label-tech">Recent quotations</div>
              <button
                onClick={() => navigate("/quotations")}
                className="btn-ghost text-xs uppercase tracking-wider"
                data-testid="view-all-quotations"
              >
                View all →
              </button>
            </div>
            {recent.length === 0 ? (
              <div className="p-10 text-center text-ink-muted">
                <div className="label-tech mb-2">No quotations yet</div>
                <button
                  onClick={() => navigate("/configurator")}
                  className="btn-secondary mt-2"
                >
                  Create your first configuration
                </button>
              </div>
            ) : (
              <table className="w-full grid-lines">
                <thead>
                  <tr>
                    <th>Quote #</th>
                    <th>Client</th>
                    <th className="num">Weight</th>
                    <th className="num">Price</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((q) => (
                    <tr
                      key={q.id}
                      className="cursor-pointer hover:bg-surfaceAlt"
                      onClick={() => navigate(`/quotations/${q.id}`)}
                      data-testid={`recent-row-${q.quote_number}`}
                    >
                      <td className="mono font-semibold">{q.quote_number}</td>
                      <td>{q.client?.company || q.client?.name || "—"}</td>
                      <td className="num">{fmtNum(q.total_weight_kg || q.result?.total_weight_kg || 0, 1)} kg</td>
                      <td className="num">{fmtINR(q.grand_total_price || q.total_price || q.result?.total_price || 0)}</td>
                      <td>
                        <span className="label-tech">{q.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="label-tech">{k}</span>
      <span className="mono font-semibold text-ink">{v}</span>
    </div>
  );
}
